import React from "react";
import Spaces from "./Spaces";
import RoomRecommendations from "./RoomRecommendations";
import CartModal from "./CartModal"; // Import the CartModal component
import axios from 'axios';

interface Product {
  productCode: string;
  quantity: string;
}

interface Room {
  rating: string;
  selectedProducts: Record<string, Product>;
  rejectedProducts?: Record<string, Product>;
}

interface WizardResultPageProps {
  back_url: string,
  products: any,
  spaces: any,
  recommendations: any,
  price_list: { [key: string]: { price: number, discount: number, currency: string } },
  company_logo_url: string | null,
  populate_cart_url: string,
  config_key: string,
}

interface WizardResultPageState {
  activeRoom: string;
  activeRoomRatings: { [key: string]: string };
  selectedData: { [key: string]: Room };
  isCartModalOpen: boolean;
  populatingCart: boolean;
  populatingCartError: string;
}

class WizardResultPage extends React.Component<WizardResultPageProps, WizardResultPageState> {
  constructor(props: WizardResultPageProps) {
    super(props);
    this.state = this.makeInitialState(props);
  }

  makeInitialState(props: WizardResultPageProps): WizardResultPageState {
    return {
      activeRoom: Object.keys(props.spaces)[0] + '0',
      activeRoomRatings: {},
      selectedData: {},
      isCartModalOpen: false,
      populatingCart: false,
      populatingCartError: '',
    };
  }

  get companyLogoUrl() {
    return this.props.company_logo_url || "https://res.cloudinary.com/flexit/image/upload/v1718123789/climat_wizard/example_logo_alt";
  }

  get inneklimaLogoUrl() {
    return "https://res.cloudinary.com/flexit/image/upload/v1692686239/flexitkt_bilder/inneklima_no_muf9ss.png";
  }

  get footerLogoUrl() {
    return "https://res.cloudinary.com/flexit/image/upload/v1736933943/climat_wizard/thumbnail_Megaflis_Logo_RGB_01_aicotd.png";
  }

  setRoomSelection = (roomCode: string, rating: string, productCode: string, index: number, quantity: number, reject: boolean = false) => {
    this.setState((prevState) => {
      const changedDataKey = reject ? 'rejectedProducts' : 'selectedProducts';
      const ignoredDataKey = reject ? 'selectedProducts' : 'rejectedProducts';

      const prevRoomData = prevState.selectedData[roomCode] || {};
      const prevChangedProducts = prevRoomData[changedDataKey] || {};
      const prevIgnoredProducts = prevRoomData[ignoredDataKey] || {};

      // Update changedDataKey (either selectedProducts or rejectedProducts)
      const updatedChangedProducts = {
        ...prevChangedProducts,
        [index]: prevChangedProducts[index]?.productCode === productCode ? undefined : {
          productCode: productCode,
          quantity: quantity.toString(),
        },
      };

      // Remove the product from ignoredDataKey (either selectedProducts or rejectedProducts)
      const {[index]: ignoredProduct, ...updatedIgnoredProducts} = prevIgnoredProducts;

      return {
        selectedData: {
          ...prevState.selectedData,
          [roomCode]: {
            ...prevRoomData,
            rating: rating,
            [changedDataKey]: updatedChangedProducts,
            [ignoredDataKey]: updatedIgnoredProducts,
          },
        },
      };
    });
  };


  getSelectedProductCode = (roomCode: string, rating: string, idx: number) => {
    try {
      if (this.state.selectedData[roomCode] && this.state.selectedData[roomCode].rating === rating) {
        const selectedCode = this.state.selectedData[roomCode].selectedProducts[idx]?.productCode;
        if (selectedCode) {
          return selectedCode;
        }
        const rejected = this.state.selectedData[roomCode].rejectedProducts[idx]?.productCode;
        if (rejected) {
          return "rejected"
        }
      }
      return null;
    } catch (e) {
      console.error(e);
      console.log(roomCode, rating, idx, this.state.selectedData[roomCode]);
      return null;
    }
  };

  getSelectedRatingForRoom = (roomCode: string): string => {
    return this.state.activeRoomRatings[roomCode] || 'better';
  };

  setRating = (roomCode: string, rating: string) => {
    this.setState((prevState) => ({
      activeRoomRatings: {
        ...prevState.activeRoomRatings,
        [roomCode]: rating,
      },
    }));
  };

  toggleCartModal = () => {
    this.setState((prevState) => ({
      isCartModalOpen: !prevState.isCartModalOpen
    }));
  };

  proceedToCartCallBack = () => {
    debugger
    this.setState({populatingCart: true, populatingCartError: ''});
    // transform selectedData to object with productCode as key and quantity as value
    const selectedProducts = Object.values(this.state.selectedData)
      .flatMap(room => Object.values(room.selectedProducts))
      .filter(product => product)
      .reduce((acc, product) => {
        const productCode = product.productCode;
        const quantity = parseInt(product.quantity, 10);

        if (acc[productCode]) {
          acc[productCode] += quantity;
        } else {
          acc[productCode] = quantity;
        }

        return acc;
      }, {});
    // make api request to add products to cart and get cart id in response
    axios.post(
      this.props.populate_cart_url,
      {config_key: this.props.config_key, products: selectedProducts},
      {
        headers: {  // Headers need to be inside a `headers` object
          'Content-Type': 'application/json',
          'X-CSRFToken': window.csrfToken
        }
      }
    ).then(response => {
      // redirect to cart page
      console.log(response)
      window.location.href = response.data.redirect_url;
    }).catch(error => {
      console.error('Error adding products to cart', error);
      this.setState({
        populatingCart: false,
        populatingCartError: error.response?.data?.error || 'An error occurred while adding products to cart'
      });
    })
  };

  getPrice = (productCode: string) => {
    const priceData = this.props.price_list[productCode];
    if (priceData) {
      return priceData;
    }
    // Generate random price if not available in price_list
    return {price: 0, discount: 0, currency: ''};
  };

  renderNavBar() {
    return (
      <div className="flex justify-center px-4 py-3">
        <img src={this.inneklimaLogoUrl} alt="Left Logo" className="h-8 w-auto"/>
      </div>
    );
  }

  renderSpaceBreadcrumbs() {
    return (
      <Spaces
        spaces={this.props.spaces}
        activeRoom={this.state.activeRoom}
        setActiveRoom={(roomType: string) => {
          this.setState({activeRoom: roomType});
        }}
        selectedData={this.state.selectedData}
        recommendations={this.props.recommendations}
      />
    );
  }

  renderRecommendations() {
    const {recommendations} = this.props;
    const {activeRoom} = this.state;
    const roomType = activeRoom.replace(/[0-9]/g, ''); // Extract the room type without the index
    const selectedRating = this.getSelectedRatingForRoom(activeRoom);

    return (
      <RoomRecommendations
        roomCode={activeRoom}
        spaceData={recommendations[roomType]}
        selectedRating={selectedRating}
        setRating={this.setRating}
        getSelectedProductCode={this.getSelectedProductCode}
        setRoomSelection={this.setRoomSelection}
        productData={this.props.products}
        getPrice={this.getPrice}
      />
    );
  }

  renderSelectedProductInfo() {
    const {selectedData} = this.state;
    let selectedInfo = null;
    const selected = Object.keys(selectedData).length > 0 && Object.keys(selectedData).some((key) => Object.keys(selectedData[key].selectedProducts).length > 0 && Object.keys(selectedData[key].selectedProducts).some((idx) => !!selectedData[key].selectedProducts[idx]));
    if (!selected) {
      selectedInfo = <div className="inline-block bg-yellow-100 text-yellow-800 py-2 px-4 my-2 rounded-md">
        <p className="font-bold">
          {window.gettext("Click on room and select a product!")}
        </p>
      </div>
    } else {
      const totalSelectedProducts = Object.values(selectedData).reduce((sum: number, room) => {
        return sum + Object.values(room.selectedProducts).reduce((roomSum: number, product: Product) => {
          return roomSum + parseInt(product?.quantity || "0", 10);
        }, 0);
      }, 0);
      const roomsWithProducts = Object.values(selectedData).reduce((count, room) => {
        const hasProductWithQuantity = Object.values(room.selectedProducts).some(product => parseInt(product?.quantity || "0", 10) > 0);
        return hasProductWithQuantity ? count + 1 : count;
      }, 0);
      selectedInfo = <div className="inline-block bg-fxblue py-2 px-4 my-2 rounded-md">
        <p className="text-white cursor-pointer" onClick={this.toggleCartModal}>
          {window.interpolate(
            ngettext('%(nproducts)s product selected', '%(nproducts)s products selected', totalSelectedProducts),
            {nproducts: totalSelectedProducts},
            true
          )}&nbsp;
          {window.interpolate(
            ngettext('for %(nrooms)s room', 'for %(nrooms)s rooms', roomsWithProducts),
            {nrooms: roomsWithProducts},
            true
          )}
        </p>
      </div>
    }
    return <div className="text-center pb-4">
      {selectedInfo}
    </div>
  }

  renderMainContent() {
    return (
      <>
        <section className="bg-white">
          <div className="container">
            {/* <div className="text-center mt-3">
              <a href={this.props.back_url} className="text-[#217CBF] text-[16px] underline">
                {window.gettext("Back to the form")}
              </a>
            </div> */}
            <h2
              className="hidden sm:block leading-snug tracking-tight text-xl md:text-2xl lg:text-3xl font-semibold mt-5 mb-0 text-center">
              {window.gettext("Select products for rooms")}
            </h2>
            <p className="hidden md:block leading-7 text-gray-700 py-6 lg:text-center mx-auto ">
              {window.gettext(
                "We have now found suggestions for your project. Click on the room and select a product " +
                "for each. Note that for some rooms, you can choose between a \"good\" and \"better\" solution."
              )}
              {/*<br/>*/}
              {/*<a href="http://inneklimaguiden.no" className="text-[#217CBF] underline">*/}
              {/*  {window.gettext("More about the best solution.")}*/}
              {/*</a>*/}
            </p>
            {this.renderSpaceBreadcrumbs()}
            {this.renderSelectedProductInfo()}

          </div>
        </section>
        <section className="bg-[#F7F7F7]">
          <div className="container max-w-5xl">
            <div
              className="text-center grid pt-5 pb-10 gap-10 my-0 mx-auto px-0 sm:px-2 md:px-4 2xl:px-6 print:max-w-none">
              {this.renderRecommendations()}
            </div>
          </div>
        </section>
      </>
    );
  }

  renderFooter() {
    const handleBackClick = (e) => {
      e.preventDefault();
      const confirmation = window.confirm("All selected data in the form will be lost. Do you want to continue?");
      if (confirmation) {
        window.location.href = this.props.back_url;
      }
    };

    const handleLogoClick = (e) => {
      e.preventDefault();
      const confirmation = window.confirm("Are you sure you want to leave without completing the form?");
      if (confirmation) {
        window.location.href = this.props.referrer_url;
      }
    };

    return (
      <div className="fixed bottom-0 left-0 w-full bg-white border-t border-gray-300">
        <div className="max-w-5xl mx-auto flex justify-between items-center px-4 py-3">

          {/* Back Link */}
          <div className="w-1/3 text-left">
            <a href="#" onClick={handleBackClick} className="text-blue-500">Tilbake</a>
          </div>

          {/* Center section with logo */}
          <div className="w-1/3 flex justify-center">
            <button onClick={handleLogoClick} className="focus:outline-none">
              <img
                src={this.footerLogoUrl}
                alt="Logo"
                className="h-8 w-auto"
              />
            </button>
          </div>

          {/* Forward Link */}
          <div className="w-1/3 text-right">
            <a href="#" className="text-blue-500" onClick={this.toggleCartModal}>Fortsett</a>
          </div>
        </div>
      </div>
    );
  }


  render() {
    return [
      this.renderNavBar(),
      <div id="scroll_to_this_place" className="bg-[#00aeef] h-[2px] w-100" key='scrollto'></div>,
      this.renderMainContent(),
      this.renderFooter(),
      this.state.isCartModalOpen && (
        <CartModal
          selectedData={this.state.selectedData}
          products={this.props.products}
          spaces={this.props.spaces}
          price_list={this.props.price_list}
          closeModal={this.toggleCartModal}
          handleProceedToCart={this.proceedToCartCallBack}
          populatingCart={this.state.populatingCart}
          populatingCartError={this.state.populatingCartError}
        />
      ),
    ];
  }
}

export default WizardResultPage;
